import { FC, useState } from 'react'
import { ReactComponent as AnalyzingIcon } from 'src/assets/svg/analyzing-48.svg'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as ComparisonReportIcon } from 'src/assets/svg/comparison-report-64.svg'
import { ReactComponent as EmptyComparisonIcon } from 'src/assets/svg/empty-comparison-48.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning-triangle-48.svg'
import { ComparisonHistoryPanel } from 'src/components/ComparisonHistoryPanel'
import { ContentComparisonApiService } from 'src/services/contentComparisonApi'
import { Report } from 'src/charts/YourReports/interfaces/report.interface'
import {
  useCreateContentComparisonMutation,
  useDeleteContentComparisonMutation,
} from 'src/reactQuery/useContentComparisonMutations'
import { useGetContentComparisonQuery } from 'src/reactQuery/useGetContentComparisonQuery'
import { useGetContentComparisonsQuery } from 'src/reactQuery/useGetContentComparisonsQuery'
import { ComparingCampaigns } from 'src/pages/Campaigns/CampaignComparison/MultiComparison/ComparingCampaigns'
import {
  CampaignComparisonPayload,
  ComparisonCampaign,
  NewCampaignComparisonModal,
} from 'src/pages/Campaigns/CampaignComparison/NewCampaignComparisonModal'
import { cn } from 'src/utils/cn'
import styles from './contentComparison.module.scss'
import { CommentVolume } from './CommentVolume'
import { ComparativeOverview } from './ComparativeOverview'
import { ContentFormatComparison } from './ContentFormatComparison'
import { PublishingIntensityMap } from './PublishingIntensityMap'
import { PostVolumeByPlatform } from './PostVolumeByPlatform'
import { ContentHeaderOptions } from '../ContentAnalysisHeader'

interface ContentComparisonProps {
  // Everything the account has; the modal filters and limits the selection.
  campaigns: ComparisonCampaign[]
  // The page's header, rendered by this view so each screen can name its own
  // way back: the list returns to the overview, a report returns to the list.
  header: (options?: ContentHeaderOptions) => JSX.Element
  onExitComparison: () => void
}

/*
 * Content Analysis → Comparisons.
 *
 * Several campaigns' published posts and comments over one window. The states,
 * the picker and the list are Social Listening's — the same run, the same
 * states, the same list of them — so only the report below is this feature's
 * own.
 *
 * Every screen here reads the server: the list, the report and the deck all
 * come from /v1_private/content-comparisons, and the report is recomputed on
 * each read rather than stored — so reopening one shows the posts and comments
 * that have landed since.
 */
const COPY = {
  compareLongerPeriods: 'Compare Longer Periods',
  emptyStateSubtitle:
    'Choose your time period and select 2 or more campaigns to get started.',
  emptyStateTitle: 'Start Comparing Your Data',
  exportComparison: 'Export Comparison',
  exporting: 'Preparing…',
  failedStateSubtitle:
    'An unexpected error occurred while generating your comparison report. ' +
    'Please try again. If the issue persists, contact support.',
  failedStateTitle: 'Analysis Failed',
  generateAgain: 'Generate Again',
  generatingSubtitle:
    "We're analyzing your selected data to generate meaningful comparison " +
    'insights. This may take a few hours depending on the amount of data ' +
    'being processed.',
  generatingTitle: 'Analyzing Your Data & Generating Comparison Report',
  listAdd: 'Add New Comparison',
  listDeleteTarget: 'this comparison',
  listPill: 'Content Comparison',
  listSubtitle: 'Compare published content across the campaigns you selected.',
  listTitle: 'Reporting & Comparison',
  // Distinct from a short period: the range may be long and the analyses
  // valid, and still one of them published nothing in it.
  noDataSubtitle:
    'None of the selected analyses published anything in this period, so ' +
    'there is nothing to compare. Try a longer period, or analyses that were ' +
    'collecting during it.',
  noDataTitle: 'Not Enough Data to Compare',
  reportTitle: 'Compare Campaigns',
  shortPeriodSubtitle:
    'The selected date ranges are too short to generate reliable comparison ' +
    'insights. For more meaningful trends and recommendations, compare longer ' +
    'periods such as a month or a quarter.',
  shortPeriodTitle: 'Short Comparison Period',
  startComparison: 'Start Comparison',
  trail: 'Comparisons',
}

// A few days gives the report nothing to trend on, so the client stops before
// spending a request to find that out. The same floor the other comparisons
// use.
// Only used if the response's Content-Disposition does not survive: the server
// names the deck after the analyses and the window.
const DECK_FILENAME = 'content-comparison.pdf'

const MIN_COMPARISON_DAYS = 7
const MS_PER_DAY = 24 * 60 * 60 * 1000

export const ContentComparison: FC<ContentComparisonProps> = ({
  campaigns,
  header,
  onExitComparison,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isShortPeriod, setIsShortPeriod] = useState(false)
  const [openedId, setOpenedId] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  const { data: list, isLoading: isListLoading } =
    useGetContentComparisonsQuery()
  const { data: opened, isError: hasFailed } = useGetContentComparisonQuery(
    openedId ?? undefined
  )

  const createComparison = useCreateContentComparisonMutation()
  const deleteComparison = useDeleteContentComparisonMutation()

  const saved = list?.data ?? []

  // The report is computed by the server on read, so the wait belongs to the
  // request rather than to a run: the analysing screen shows while it is in
  // flight and while a new definition is being saved.
  const isGenerating =
    createComparison.isPending || (!!openedId && !opened && !hasFailed)

  const nameOf = (id: string) =>
    campaigns.find((campaign) => campaign.id === id)?.name ?? id

  const handleSubmit = ({
    campaignIds,
    from,
    quickCompare,
    to,
  }: CampaignComparisonPayload) => {
    // Inclusive of both endpoints, so Jul 1 – Jul 2 counts as 2 days.
    const days =
      Math.round(
        (new Date(to).getTime() - new Date(from).getTime()) / MS_PER_DAY
      ) + 1

    setIsModalOpen(false)

    if (days < MIN_COMPARISON_DAYS) {
      setIsShortPeriod(true)

      return
    }

    setIsShortPeriod(false)

    createComparison.mutate(
      {
        analyses: campaignIds,
        quickCompare,
        // Calendar dates, which is what the endpoint documents: the picker
        // hands back ISO instants, and the day is the part that matters.
        window: { from: from.slice(0, 10), to: to.slice(0, 10) },
      },
      // Straight into the report it just saved, rather than back to a list the
      // reader would then have to find it in.
      { onSuccess: (response) => setOpenedId(response.data.id) }
    )
  }

  const handleExport = async () => {
    if (!openedId) return

    setIsExporting(true)

    try {
      await ContentComparisonApiService.download(openedId, DECK_FILENAME)
    } finally {
      // Cleared whether or not the render succeeded: leaving the button in its
      // preparing state would make a failed export look like one still running.
      setIsExporting(false)
    }
  }

  const modal = (
    <NewCampaignComparisonModal
      campaigns={campaigns}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onSubmit={handleSubmit}
    />
  )

  // The states share a card, an icon, a heading, a sentence and at most one
  // button, so they are one function rather than four copies of it.
  const stateCard = (
    icon: JSX.Element,
    title: string,
    subtitle: string,
    action?: { label: string; onClick: () => void }
  ) => (
    <div className={styles.card}>
      {header({ embedded: true, onBack: onExitComparison })}

      <div className={styles.emptyState}>
        {icon}
        <h3 className={styles.emptyStateTitle}>{title}</h3>
        <p className={styles.emptyStateSubtitle}>{subtitle}</p>

        {action && (
          <button
            className={styles.startButton}
            onClick={action.onClick}
            type="button"
          >
            {action.label}
          </button>
        )}
      </div>

      {modal}
    </div>
  )

  if (isGenerating) {
    return (
      <div className={styles.card}>
        {header({ embedded: true, onBack: onExitComparison })}

        <div className={styles.emptyState}>
          <AnalyzingIcon className={styles.generatingIcon} />
          <h3 className={styles.emptyStateTitle}>{COPY.generatingTitle}</h3>
          <p className={styles.emptyStateSubtitle}>{COPY.generatingSubtitle}</p>
        </div>
      </div>
    )
  }

  if (isShortPeriod) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
      />,
      COPY.shortPeriodTitle,
      COPY.shortPeriodSubtitle,
      {
        label: COPY.compareLongerPeriods,
        onClick: () => {
          setIsShortPeriod(false)
          setIsModalOpen(true)
        },
      }
    )
  }

  if (hasFailed) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconFailed)}
      />,
      COPY.failedStateTitle,
      COPY.failedStateSubtitle,
      {
        label: COPY.generateAgain,
        onClick: () => {
          // Back to the list rather than straight into the picker: the report
          // that failed is still saved, and may simply have lost an analysis.
          setOpenedId(null)
        },
      }
    )
  }

  // The report.
  if (opened) {
    const report = opened.data.result

    // Refused only when there is nothing at all to show. One analysis at zero
    // is not a reason to withhold the other two — that it published nothing in
    // the window is itself worth seeing, and the widgets carry it honestly as
    // an empty column. What it must NOT do is get ranked: see the badge below.
    const hasData = report.comparativeOverview.some(
      (card) => card.totalPosts > 0
    )

    if (!hasData) {
      return stateCard(
        <WarningTriangleIcon
          className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
        />,
        COPY.noDataTitle,
        COPY.noDataSubtitle,
        {
          label: COPY.compareLongerPeriods,
          onClick: () => setOpenedId(null),
        }
      )
    }

    return (
      <>
        {header({ onBack: () => setOpenedId(null), trail: COPY.trail })}

        <div className={styles.titleRow}>
          <h2 className={styles.reportTitle}>{COPY.reportTitle}</h2>

          <div className={styles.titleActions}>
            <span className={styles.period}>
              <CalendarIcon className={styles.periodIcon} />
              <span className={styles.periodDates}>{report.periodLabel}</span>
            </span>

            {/* The deck is rendered on demand, so this takes as long as a
                render; the button says so rather than looking inert. */}
            <button
              className={styles.exportButton}
              disabled={isExporting}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon className={styles.exportIcon} />
              {isExporting ? COPY.exporting : COPY.exportComparison}
            </button>
          </div>
        </div>

        <ComparingCampaigns campaigns={report.campaigns} />

        <PostVolumeByPlatform
          campaigns={report.campaigns}
          highest={report.postVolume.highest}
          lowest={report.postVolume.lowest}
          platforms={report.postVolume.platforms}
        />

        <CommentVolume
          byPlatform={report.commentVolume.byPlatform}
          bySentiment={report.commentVolume.bySentiment}
          campaigns={report.campaigns}
          insight={report.commentVolume.insight}
          topComments={report.commentVolume.topComments}
        />

        <ContentFormatComparison
          campaigns={report.campaigns}
          insight={report.contentFormat.insight}
          rows={report.contentFormat.rows}
        />

        <PublishingIntensityMap
          campaigns={report.campaigns}
          dayCount={report.publishingIntensity.dayCount}
          endLabel={report.publishingIntensity.endLabel}
          midLabel={report.publishingIntensity.midLabel}
          rows={report.publishingIntensity.rows}
          startLabel={report.publishingIntensity.startLabel}
        />

        <ComparativeOverview
          campaigns={report.campaigns}
          cards={report.comparativeOverview}
          periodLabel={report.periodLabel}
        />

        {modal}
      </>
    )
  }

  // The list of saved comparisons.
  if (saved.length > 0) {
    const rows: Report[] = saved.map((row) => ({
      dateRange: row.periodLabel,
      generatedAt: new Date(row.createdAt).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
      id: row.id,
      modules: [],
      name: row.analyses.map(nameOf).join(' — '),
      status: 'ready',
    }))

    return (
      <>
        {header({ onBack: onExitComparison })}

        <div className={cn(styles.card, styles.cardList)}>
          <ComparisonHistoryPanel
            addLabel={COPY.listAdd}
            deleteTarget={COPY.listDeleteTarget}
            icon={ComparisonReportIcon}
            onAdd={() => setIsModalOpen(true)}
            onDelete={(id) => deleteComparison.mutate(id)}
            onDownload={(id) =>
              ContentComparisonApiService.download(id, DECK_FILENAME)
            }
            onOpen={setOpenedId}
            // A saved definition has no run left to fail, so the panel's retry
            // branch never renders.
            onRetry={() => undefined}
            pillLabel={COPY.listPill}
            rowCopy={{
              failedSubtext: '',
              failedTitle: (report: Report) => report.name,
              generating: 'Generating comparison…',
              retry: 'Retry Generate Comparison',
            }}
            rows={rows}
            subtitle={COPY.listSubtitle}
            title={COPY.listTitle}
          />
        </div>

        {modal}
      </>
    )
  }

  // Nothing at all until the list has answered: the empty state says the
  // account has no comparisons, which is not yet known.
  if (isListLoading) {
    return (
      <div className={styles.card}>
        {header({ embedded: true, onBack: onExitComparison })}
      </div>
    )
  }

  return stateCard(
    <EmptyComparisonIcon className={styles.emptyStateIcon} />,
    COPY.emptyStateTitle,
    COPY.emptyStateSubtitle,
    { label: COPY.startComparison, onClick: () => setIsModalOpen(true) }
  )
}
